import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NovellResponse } from 'containers/Scene/types';
import { DEFAULT_BUBBLE } from 'custom-constants';
import { Bubble, ID } from 'domain/bubble';
import { RootState } from '../store';

export interface SelectedBubbleState {
  bubble: Bubble;
  bg: ID;
  border: ID;
  person: ID;
  sound: string;
  activeStep: ID;
  attrs: Record<ID, number>;
  novell: NovellResponse;
  loadedNovell: boolean;
  errorNovell: boolean;
}

const defaultVal = 0;

const initialState: SelectedBubbleState = {
  bubble: DEFAULT_BUBBLE,
  bg: defaultVal,
  border: defaultVal,
  person: defaultVal,
  sound: '',
  activeStep: defaultVal,
  attrs: {},
  loadedNovell: false,
  errorNovell: false,
  novell: {
    steps: [],
    bubbles: [],
  },
};

export const loadNovell = createAsyncThunk('selectedBubble/loadNovell', async () => {
  if (window.novellStory && window.novellStory.novell) {
    if (typeof window.novellStory.novell === 'string') {
      const res = await fetch(window.novellStory.novell);
      if (res.status !== 200) {
        throw new Error();
      }
      const NOVELL = (await res.json()) as NovellResponse;
      return NOVELL;
    }
    return window.novellStory.novell;
  }
  throw new Error();
});

export const selectedBubbleSlice = createSlice({
  name: 'selectedBubble',
  initialState,
  extraReducers: {
    [loadNovell.rejected.type]: (state) => {
      state.loadedNovell = false;
      state.errorNovell = true;
    },
    [loadNovell.fulfilled.type]: (state, action) => {
      const step = action.payload.steps[0];
      state.novell = action.payload;
      state.activeStep = 0;
      state.loadedNovell = true;
      state.bg = action.payload.bubbles[step.bubbleId].background || 0;
      state.sound = action.payload.bubbles[step.bubbleId].sound;
      state.border = action.payload.bubbles[step.bubbleId].border;
      state.person = action.payload.bubbles[step.bubbleId].person;
      state.bubble = action.payload.bubbles[step.bubbleId];
    },
  },
  reducers: {
    nextStep: (state, action: PayloadAction<{ oIdx: ID }>) => {
      const { oIdx } = action.payload;
      const stepId = state.activeStep;

      let step = state.novell.steps[stepId];
      let activeStep = null;

      if (oIdx >= 0 && step?.attr) {
        state.attrs[stepId] = oIdx;
      }

      if (step?.depAttr !== undefined && step?.depAttr !== step.id) {
        activeStep = step.branches[state.attrs[step?.depAttr]];
      } else if (step.branches.length > 1) {
        activeStep = step.branches[oIdx];
      } else {
        [activeStep] = step.branches;
      }

      state.activeStep = activeStep;
      step = state.novell.steps[activeStep];
      state.bg = state.novell.bubbles[step.bubbleId].background ?? state.bg;
      state.sound =
        state.novell.bubbles[step.bubbleId].sound === ''
          ? state.sound
          : state.novell.bubbles[step.bubbleId].sound ?? state.sound;
      state.border = state.novell.bubbles[step.bubbleId].border;
      state.person = state.novell.bubbles[step.bubbleId].person;
      state.bubble = state.novell.bubbles[step.bubbleId];
    },
  },
});

export const { nextStep } = selectedBubbleSlice.actions;

export const getSelectedBubble = (state: RootState) => state.selectedBubble.bubble;
export const getSelectedBG = (state: RootState) => state.selectedBubble.bg;
export const getSelectedSound = (state: RootState) => state.selectedBubble.sound;
export const getSelectedBorder = (state: RootState) => state.selectedBubble.border;
export const getSelectedPerson = (state: RootState) => state.selectedBubble.bubble.persons;
export const getLoadedNovell = (state: RootState) => state.selectedBubble.loadedNovell;
export const getErrorNovell = (state: RootState) => state.selectedBubble.errorNovell;

export const selectedBubbleReducer = selectedBubbleSlice.reducer;
